import type { Navigator, Screen } from "../app/screen";
import { note, pageShell } from "../ui/page";
import { animatePlates, type PlateAnimator } from "../fx/plate";

/**
 * SCREENS THE MENU OFFERS THAT ARE NOT BUILT YET.
 *
 * ⭐ **THIS FILE IS THE HONEST MIDDLE GROUND BETWEEN A DEAD END AND A LIE.** The rebuild's whole
 * premise is that a player must never press something and land nowhere. Until each of these is
 * written, the screen exists, says plainly that it is not finished, and offers BACK. That is a
 * legitimate state; a control that routes into nothing is not.
 *
 * ⛔ **DO NOT LET A STUB QUIETLY BECOME THE PRODUCT.** `tools/verify_web_screens.mjs` counts them and
 * prints the list, so "how much is actually built" is a number nobody has to estimate. A stub with no
 * counter is how six screens once sat at zero while the project reported progress.
 *
 * ⚠️ Each one names the ROUTE it will need, so the work is scoped rather than rediscovered:
 * the REST routes already exist on our server and are declared in `net/api.ts`.
 */

interface StubSpec {
  id: string;
  title: string;
  sub: string;
  /** What still has to be built here, in one sentence a future session can act on. */
  todo: string;
}

const SPECS: StubSpec[] = [
  // ⭐ `leagues` is no longer a stub - see `screens/play.ts`. Removed rather than left behind:
  // two screens claiming one id is a second owner, and the registry would silently pick one.
  // ⭐ `duels` is a real screen now - see `screens/duels.ts`.
  // ⭐ `decks` is a real screen now - see `screens/decks.ts`.
  // ⭐ `profile` is a real screen now - see `screens/profile.ts`.
  // ⭐ `ranks` is a real screen now - see `screens/ranks.ts`.
  // ⭐ `settings` is not a screen at all - the sound controls are an OVERLAY (`ui/settings.ts`),
  // because a player changes the volume mid-match and a route would abandon the turn.
  // ⭐ `help` is a real screen now - generated from the served rules; see `screens/help.ts`.
];

function makeStub(spec: StubSpec): Screen {
  let anim: PlateAnimator | null = null;
  return {
    id: spec.id,
    mustOffer: ["BACK"],

    mount(host: HTMLElement, nav: Navigator): void {
      const { el, body } = pageShell(nav, spec.title, spec.sub);
      body.append(
        note("This screen is not built yet."),
        note(spec.todo),
      );
      host.append(el);
      anim = animatePlates(el);
    },

    unmount(): void {
      anim?.stop();
      anim = null;
    },
  };
}

export const StubScreens: Screen[] = SPECS.map(makeStub);

/** Ids of every screen that is still a stub. Read by `tools/verify_web_screens.mjs`. */
export const StubIds: readonly string[] = SPECS.map((s) => s.id);
